import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TablePagination, Chip, IconButton, MenuItem, InputAdornment } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import api from '../../api';

const TeamReportSection = ({ empId, team, shifts }) => {
  const defaultDate = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('ALL');
  const [teamReport, setTeamReport] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const depts = [...new Set(team.map(t => t.department).filter(Boolean))];
    setDepartments(depts);
  }, [team]);

  useEffect(() => {
    api.get(`/roster/daily-report/${empId}`, { params: { start_date: startDate, end_date: endDate } })
      .then(res => setTeamReport(res.data))
      .catch(err => console.warn(err));
  }, [empId, startDate, endDate]);

  const filteredReport = teamReport.filter(row => {
    const empObj = team.find(t => t.emp_id === row.emp_id);
    const empName = empObj ? `${empObj.first_name} ${empObj.last_name}` : '';
    const empDept = empObj ? empObj.department : '';
    
    const matchesSearch = row.emp_id.toLowerCase().includes(search.toLowerCase()) || empName.toLowerCase().includes(search.toLowerCase());
    const matchesDept = department === 'ALL' || empDept === department;
    
    return matchesSearch && matchesDept;
  });

  const exportToExcel = () => {
    if (filteredReport.length === 0) return;
    const exportData = filteredReport.map(row => {
      const empObj = team.find(t => t.emp_id === row.emp_id);
      const shiftObj = shifts.find(s => s.id === row.shift_id);
      return {
        'Date': row.roster_date || startDate,
        'Emp ID': row.emp_id,
        'Name': empObj ? `${empObj.first_name} ${empObj.last_name}` : 'Unknown',
        'Department': empObj ? empObj.department : 'N/A',
        'Assigned Shift': shiftObj ? shiftObj.shift_name : 'Unallocated'
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Team_Roster_Report");
    XLSX.writeFile(workbook, `Team_Roster_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Daily Team Shift Report
        </Typography>
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={startDate} onChange={e => setStartDate(e.target.value)} sx={{ minWidth: 140, bgcolor: 'white' }} />
          <TextField type="date" label="End Date" InputLabelProps={{ shrink: true }} value={endDate} onChange={e => setEndDate(e.target.value)} sx={{ minWidth: 140, bgcolor: 'white' }} />
          
          <TextField 
            placeholder="Search ID/Name..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 200, bgcolor: 'white' }}
          />

          <TextField select label="Department" value={department} onChange={e => setDepartment(e.target.value)} sx={{ minWidth: 150, bgcolor: 'white' }}>
            <MenuItem value="ALL">All Departments</MenuItem>
            {departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="success" onClick={exportToExcel} title="Export Filtered to Excel" sx={{ bgcolor: '#e8f5e9' }}>
            <DownloadIcon />
          </IconButton>
        </Box>
        
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Emp ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Assigned Shift</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReport.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>No roster data found for these filters.</TableCell></TableRow>
              ) : (
                filteredReport.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => {
                  const empObj = team.find(t => t.emp_id === row.emp_id);
                  const employeeName = empObj ? `${empObj.first_name} ${empObj.last_name}` : 'Unknown';
                  const empDept = empObj ? empObj.department : '--';
                  const shiftObj = shifts.find(s => s.id === row.shift_id);
                  const shiftName = shiftObj ? shiftObj.shift_name : 'Unallocated';
                  
                  return (
                    <TableRow key={`${row.emp_id}-${row.roster_date}`} hover>
                      <TableCell>{row.roster_date || startDate}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{row.emp_id}</TableCell>
                      <TableCell>{employeeName}</TableCell>
                      <TableCell>{empDept}</TableCell>
                      <TableCell>
                        <Chip label={shiftName} color={shiftName === 'Unallocated' ? 'default' : 'primary'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredReport.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>
    </Box>
  );
};

export default TeamReportSection;
